import { usePatronSelector } from './use-patron-selector';
import { useCallback } from 'react';

/**
 * Hook that provides a function to strip the program path from a URL path.
 * This is useful when navigating internally with React Router, as the router
 * will automatically prepend the program path (brand/program slugs).
 */
export function useProgramPathStripper() {
  const programPath = usePatronSelector(
    (state) => state.appConfig.program_path
  );

  return useCallback(
    (path: string) => {
      if (!programPath || !path) {
        return path;
      }

      // Normalize program path to ensure it starts with /
      let normalizedProgramPath = programPath;
      if (!normalizedProgramPath.startsWith('/')) {
        normalizedProgramPath = `/${normalizedProgramPath}`;
      }

      // Remove trailing slash from program path if present
      normalizedProgramPath = normalizedProgramPath.replace(/\/$/, '');

      // Create regex to match the program path at the beginning of the path
      // Use word boundary or path separator to ensure exact match
      const escapedProgramPath = normalizedProgramPath.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );
      const programPathRegex = new RegExp(`^${escapedProgramPath}(?=/|$)`, 'i');

      // Strip the program path
      const strippedPath = path.replace(programPathRegex, '');

      // Ensure we always return a path that starts with / (unless empty)
      if (!strippedPath) {
        return '/';
      }
      return strippedPath.startsWith('/') ? strippedPath : `/${strippedPath}`;
    },
    [programPath]
  );
}
